import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { useVerifyEmail } from '@/auth/hooks';
import { errorMessage } from '@/lib/errorMessage';
import { ROUTES } from '@/lib/config';
import { useT } from '@/i18n/useT';

/**
 * Landing page for the emailed verification link (/app/verify?token=…). It
 * confirms the token automatically on mount, then points the customer at login
 * with a clear, full-width call-to-action button.
 */
export function VerifyPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const verify = useVerifyEmail();
  const [status, setStatus] = useState<'pending' | 'done' | 'error'>('pending');
  const [serverError, setServerError] = useState('');
  // The verify token is single-use; StrictMode double-invokes effects in dev, so
  // guard against a second POST consuming-then-erroring the just-used token.
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    verify
      .mutateAsync({ token })
      .then(() => setStatus('done'))
      .catch((e) => {
        setServerError(errorMessage(e));
        setStatus('error');
      });
  }, [token, verify]);

  const goToLogin = <Button onClick={() => navigate(ROUTES.login)}>{t('verify.goToLogin')}</Button>;

  if (!token) {
    return (
      <Card title={t('verify.title')}>
        <Alert kind="error">{t('verify.missingToken')}</Alert>
        {goToLogin}
      </Card>
    );
  }

  return (
    <Card title={t('verify.title')} subtitle={status === 'pending' ? t('verify.working') : undefined}>
      {status === 'pending' && <Spinner />}
      {status === 'done' && (
        <>
          <Alert kind="success">{t('verify.success')}</Alert>
          {goToLogin}
        </>
      )}
      {status === 'error' && (
        <>
          <Alert kind="error">{serverError}</Alert>
          {goToLogin}
        </>
      )}
    </Card>
  );
}
